import {
    CellEditingStoppedEvent,
    CellPosition,
    Column,
    NavigateToNextCellParams,
    TabToNextCellParams
} from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { RefObject, useCallback, useMemo } from 'react';
import { notNull } from '../../../utils';
import { getNextColumnPosition } from '../cellFunctions';

export declare interface DataGridHandle {
    element: HTMLDivElement | null;
    scrollToColumn: (colIdx: number) => void;
    scrollToRow: (rowIdx: number) => void;
    selectCell: (position: Position, openEditor?: boolean) => void;
}

declare interface Position {
    idx: number;
    rowIdx: number;
}

export const useAgGridTabbing = <T>(columns: Column[], rows: T[], gridRef: RefObject<AgGridReact>) => {
    const editableColumns = useMemo(() => {
        return columns.map(c => (c.getColDef().editable ? c : null)).filter(notNull);
    }, [columns]);

    const handleKeyDown = event => {
        const focusedCell = gridRef.current?.api.getFocusedCell();
        const editingCells = gridRef.current?.api.getEditingCells();

        let nextCell = null as CellPosition | null;
        const excludedKeys = ['Tab', 'Enter', 'ArrowUp', 'ArrowDown'];
        if (excludedKeys.includes(event.event.key)) {
            event.event.preventDefault();
        }

        //Only apply the below logic if we're editing
        if (focusedCell == null || editingCells?.length == 0) {
            return;
        }
        if (event.event.key === 'ArrowUp') {
            nextCell = { ...focusedCell, rowIndex: focusedCell.rowIndex - 1 };
        } else if (event.event.key === 'ArrowDown') {
            nextCell = { ...focusedCell, rowIndex: focusedCell.rowIndex + 1 };
        } else if (event.event.key === 'Tab') {
            // const rowNode = event.api.getDisplayedRowAtIndex(focusedCell.rowIndex);
            event.api.stopEditing();
            nextCell = focusedCell;
        }
        // } else if (event.event.key === 'ArrowLeft') {
        //     nextCell = getNextColumnPosition(rows.length, editableColumns, true, focusedCell);
        // } else if (event.event.key === 'ArrowRight') {
        //     nextCell = getNextColumnPosition(rows.length, editableColumns, false, focusedCell);
        // }
        if (nextCell?.column != null) {
            gridRef?.current?.api?.setFocusedCell(nextCell?.rowIndex, nextCell?.column);
        }
    };

    const tabToNextCell = useCallback(
        (params: TabToNextCellParams<T>) => {
            const previousCell = params.previousCellPosition;
            const lastRowIndex = previousCell.rowIndex;

            const columns = params.api.getAllGridColumns();
            if (lastRowIndex == -1 || editableColumns.length == 0) {
                return params.nextCellPosition;
            }

            const rowNode = params.api.getDisplayedRowAtIndex(previousCell.rowIndex);
            params.api.stopEditing();
            return getNextColumnPosition(
                params.api.getDisplayedRowCount(),
                columns,
                params.backwards,
                params.previousCellPosition,
                rowNode!
            );
        },
        [editableColumns.length]
    );

    const onCellEditingStopped = useCallback(
        (e: CellEditingStoppedEvent) => {
            // e.event?.stopPropagation();
            const rowIndex = e.rowIndex;
            const colId = e.column.getColId();
            const columns = e.api.getAllDisplayedColumns();
            const colIdx = columns.findIndex(c => c.getColId() == colId);
            if (rowIndex != undefined && colIdx != undefined) {
                setTimeout(() => {
                    gridRef.current?.api.setFocusedCell(rowIndex, colId, null);
                }, 750);
            }
        },
        [gridRef]
    );

    const navigateToNextCell = useCallback(
        (params: NavigateToNextCellParams) => {
            const KEY_LEFT = 'ArrowLeft';
            const KEY_UP = 'ArrowUp';
            const KEY_RIGHT = 'ArrowRight';
            const KEY_DOWN = 'ArrowDown';

            const previousCell = params.previousCellPosition;
            let nextRowIndex, renderedRowCount;
            switch (params.key) {
                case KEY_UP:
                    // return the cell above
                    nextRowIndex = previousCell.rowIndex - 1;
                    if (nextRowIndex < -1) {
                        return null;
                    } // returning null means don't navigate
                    return {
                        rowIndex: nextRowIndex,
                        column: previousCell.column,
                        rowPinned: previousCell.rowPinned
                    };
                case KEY_DOWN:
                    // return the cell below
                    nextRowIndex = previousCell.rowIndex + 1;
                    renderedRowCount = gridRef.current?.api.getDisplayedRowCount();
                    if (nextRowIndex >= renderedRowCount) {
                        return null;
                    } // returning null means don't navigate
                    return {
                        rowIndex: nextRowIndex,
                        column: previousCell.column,
                        rowPinned: previousCell.rowPinned
                    };
                case KEY_LEFT:
                    return params.nextCellPosition;

                case KEY_RIGHT:
                    return params.nextCellPosition;
                default:
                    throw Error('this will never happen, navigation is always one of the 4 keys above');
            }
        },
        [gridRef]
    );

    return {
        handleKeyDown,
        tabToNextCell,
        navigateToNextCell,
        onCellEditingStopped
    };
};
